<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    title: string;
    description?: string;
    descriptionToHighlight?: string;

    actionLabel?: string;
    isLoading?: boolean;
    action?: () => void;

    asset?: string;
    assetAlt?: string;
    assetSize?: "sm" | "md";
    icon?: string;

    color?: "primary" | "critical" | "secondary" | "success" | "warning";
  }>(),
  {
    description: undefined,
    descriptionToHighlight: undefined,
    isLoading: false,
    asset: undefined,
    assetAlt: undefined,
    actionLabel: undefined,
    action: undefined,
    color: "primary",
    assetSize: "md",
    icon: undefined,
  }
);

const mainPaddingClass = computed(() => {
  if (props.actionLabel || props.assetSize === "sm") {
    return "p-4";
  }
  return "p-1";
});

/**
 * Allow a specific string to be highlighted in the description.
 * Handle multiple occurrences. Case-sensitive.
 */
const parsedDescription = computed(() => {
  if (!props.description) return [];

  if (props.descriptionToHighlight) {
    const parts = props.description.split(props.descriptionToHighlight);

    for (let i = parts.length - 1; i > 0; i--) {
      parts.splice(i, 0, props.descriptionToHighlight);
    }
    return parts;
  }
  return [props.description];
});

const ui = computed(() => ({
  wrapper: {
    main: [`bg-${props.color}-50`, mainPaddingClass.value, "rounded-xl flex flex-col gap-3"],
    mainContent: "flex items-center gap-3",
    content: [props.actionLabel ? "justify-between" : "justify-center", "flex flex-col gap-2"],
    icon: "min-w-fit min-h-fit rounded-lg flex items-center justify-center",
    text: "flex flex-col gap-1",
  },
  style: {
    title: [`text-${props.color}-${props.color === "secondary" ? "900" : "500"}`, "font-semibold"],
    description: "text-gray-800",
  },
  icon: [`text-${props.color}-500`, props.icon ? "size-4" : "w-15 h-15"],
}));
</script>

<template>
  <div :class="ui.wrapper.main">
    <div :class="ui.wrapper.mainContent">
      <div :class="ui.wrapper.icon">
        <img
          v-if="asset"
          :height="assetSize === 'sm' ? 48 : 88"
          :width="assetSize === 'sm' ? 48 : 88"
          :src="importAsset(asset)"
          :alt="assetAlt || $t('common.banner.defaultAssetAlt')"
        />
        <AwIcon v-else :name="icon ?? 'i-ph-shooting-star-thin'" :class="ui.icon" />
      </div>

      <div :class="ui.wrapper.content">
        <div :class="ui.wrapper.text">
          <span :class="ui.style.title">{{ title }}</span>
          <p v-if="description" :class="ui.style.description">
            <span
              v-for="(part, index) in parsedDescription"
              :key="`${part}-${index}`"
              :class="{ 'font-semibold': part === descriptionToHighlight }"
              >{{ part }}</span
            >
          </p>
        </div>

        <template v-if="actionLabel">
          <div>
            <AwButton :color="color" :is-loading="isLoading" :label="actionLabel" size="sm" @click="action" />
          </div>
        </template>
      </div>
    </div>
    <slot name="content"></slot>
  </div>
</template>
